import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useCard, useCreateCard, useGenerateCard, type CardInput } from '@/lib/cards/api';
import { derivedCardInput } from '@/lib/cards/filiation';
import { CardInfoForm, EMPTY_CARD_INPUT } from '@/features/cards/card-info-form';
import { prefilledCardInput } from '@/lib/cards/prefill';

/**
 * Route tuteur `/tutor/cards/new` — crée la carte en brouillon avec le savoir
 * saisi, puis lance la génération IA du recto. Si la génération échoue, la
 * carte existe déjà : on ouvre quand même son détail (bouton « Générer »).
 *
 * `?parent=<id>` : carte DÉRIVÉE (filiation) — formulaire prérempli depuis la
 * carte parente (matière, niveaux, domaine…), lien posé, rareté = parente + 1.
 */
export function TutorCardsNewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { parent: parentId, ...prefill } = useSearch({ from: '/app/tutor/cards/new' });
  const parent = useCard(parentId);
  const create = useCreateCard();
  const generate = useGenerateCard();
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'generating'>('form');

  const submit = async (input: CardInput) => {
    if (!user) return;
    setError(null);
    let cardId: string;
    try {
      cardId = await create.mutateAsync({ input, userId: user.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible.');
      return;
    }
    setStep('generating');
    try {
      await generate.mutateAsync({ card_id: cardId });
      await navigate({ to: '/tutor/cards/$cardId', params: { cardId } });
    } catch (e) {
      await navigate({
        to: '/tutor/cards/$cardId',
        params: { cardId },
        search: { generationError: e instanceof Error ? e.message : 'Génération impossible.' },
      });
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <Link
        to="/tutor/cards"
        className="text-ink-500 hover:text-ink-800 mb-3 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> SKILLCARDS
      </Link>
      <h1 className="font-display text-ink-900 mb-1 text-xl font-extrabold">
        {parentId ? 'Créer une carte dérivée' : 'Créer une carte'}
      </h1>
      {parent.data && (
        <p className="bg-primary-50 text-ink-800 mb-3 rounded-xl px-3 py-2 text-sm">
          Suite plus avancée de « {parent.data.title} » : elle gagne une étoile de rareté de plus.
        </p>
      )}
      <p className="text-ink-500 mb-6 text-sm">
        Écris un simple texte : l’IA remplit les informations de la carte, puis en fait un outil
        compact au format carte à jouer (63 × 88 mm) (étapes, formule, tableau, exemple, piège). Tu
        relis et valides avant de publier.
      </p>

      {step === 'generating' ? (
        <div
          role="status"
          className="border-ink-200 flex flex-col items-center gap-3 rounded-2xl border bg-white p-8 text-center"
        >
          <Sparkles className="text-primary-500 h-8 w-8 animate-pulse" />
          <p className="text-ink-900 font-semibold">L’IA compose le recto de ta carte…</p>
          <p className="text-ink-500 text-sm">Quelques secondes.</p>
        </div>
      ) : parentId && parent.isLoading ? (
        <p className="text-ink-500 text-sm">Chargement de la carte parente…</p>
      ) : (
        <CardInfoForm
          key={parent.data?.id ?? 'new'}
          initial={
            parent.data
              ? derivedCardInput(parent.data, EMPTY_CARD_INPUT)
              : prefilledCardInput(prefill, EMPTY_CARD_INPUT)
          }
          submitLabel="Créer et générer"
          submitting={create.isPending}
          error={error}
          onSubmit={submit}
          onCancel={() => navigate({ to: '/tutor/cards' })}
        />
      )}
    </div>
  );
}
